import React, { useEffect, useRef, useState } from 'react';
import { X } from 'react-feather';

import { useKeywordGroups } from '@/Product/hooks/useKeywordGroups';
import {
  KeywordGroup,
  KeywordGroupDropdownProps,
  KeywordGroupItem,
} from '@/Product/types/productTypes';

const KeywordGroupDropdown: React.FC<KeywordGroupDropdownProps> = ({
  selectedKeywordGroups,
  onKeywordGroupChange,
  onKeywordGroupRemove,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: keywordGroupResponse, isLoading, error } = useKeywordGroups();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleKeywordGroupSelect = (keywordGroupItem: KeywordGroupItem) => {
    const keywordGroup: KeywordGroup = {
      keywordGroupId: keywordGroupItem.id,
      keywordGroupName: keywordGroupItem.group,
    };

    const isSelected = isKeywordGroupSelected(keywordGroupItem.id);
    onKeywordGroupChange(keywordGroup, !isSelected);
  };

  const isKeywordGroupSelected = (keywordGroupId: number) => {
    return selectedKeywordGroups.some(
      (keywordGroup) => keywordGroup.keywordGroupId === keywordGroupId,
    );
  };

  const getDisplayText = () => {
    if (selectedKeywordGroups.length === 0) {
      return '';
    }
    return selectedKeywordGroups
      .map((keywordGroup) => keywordGroup.keywordGroupName)
      .join(', ');
  };

  if (error) {
    return (
      <div className="relative">
        <div className="h-10 w-full rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600">
          키워드 그룹 로드 실패
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        tabIndex={0}
        role="button"
        className={`relative h-10 w-full rounded border border-[#CECECE] px-3 text-sm ${
          disabled
            ? 'cursor-not-allowed bg-gray-100'
            : 'cursor-pointer bg-white'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <input
          type="text"
          className="absolute h-full w-[90%] cursor-pointer border-none bg-transparent outline-none"
          value={getDisplayText()}
          placeholder="Click to add keyword group"
          readOnly
          disabled={disabled}
        />
      </div>

      {/* Selected Keyword Groups Tags */}
      {selectedKeywordGroups.length > 0 && (
        <div className="mt-2 flex w-full flex-wrap gap-2">
          {selectedKeywordGroups.map((keywordGroup) => (
            <div
              key={keywordGroup.keywordGroupId}
              className="flex items-center gap-2 rounded bg-[#F4ECF4] px-2 py-1"
            >
              <p className="text-xs font-normal text-[#1D1D1D]">
                {keywordGroup.keywordGroupName}
              </p>
              <button
                type="button"
                className="cursor-pointer bg-transparent"
                onClick={() =>
                  onKeywordGroupRemove(keywordGroup.keywordGroupId)
                }
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropdown positioned after tags */}
      {isOpen && !disabled && keywordGroupResponse?.data && (
        <div className="absolute z-50 mt-1 max-h-[400px] w-full overflow-y-auto rounded border border-[#CECECE] bg-white shadow-lg">
          {keywordGroupResponse.data.keywordGroupList.length > 0 ? (
            keywordGroupResponse.data.keywordGroupList.map(
              (keywordGroupItem) => (
                <div
                  tabIndex={0}
                  role="button"
                  key={keywordGroupItem.id}
                  className={`flex cursor-pointer items-center px-3 py-2 hover:bg-gray-50 ${
                    isKeywordGroupSelected(keywordGroupItem.id)
                      ? 'bg-blue-50 text-blue-700'
                      : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleKeywordGroupSelect(keywordGroupItem);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isKeywordGroupSelected(keywordGroupItem.id)}
                    onChange={() => {}}
                    className="mr-2 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="text-sm">{keywordGroupItem.group}</span>
                </div>
              ),
            )
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              키워드 그룹이 없습니다.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KeywordGroupDropdown;
